import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { useAuth } from "hooks/auth/useAuth";
import { useMFA } from "hooks/auth/useMFA";
import { useNavigate } from "react-router-dom";

interface UserTypeContent {
  description: string;
  sensitiveInfo: string[];
}

const getUserTypeContent = (role: string): UserTypeContent => {
  switch (role) {
    case "admin":
      return {
        description:
          "As an administrator, FreeTech's portal gives you access to highly sensitive information including:",
        sensitiveInfo: [
          "All client financial and banking information",
          "Freelancer personal and tax documents",
          "Confidential business contracts and agreements",
          "User account data and administrative controls",
          "Financial reporting and payment processing systems",
        ],
      };

    case "freelancer":
      return {
        description:
          "As a freelancer, FreeTech's portal contains your sensitive information including:",
        sensitiveInfo: [
          "Your personal and financial information",
          "Tax documents and direct deposit details",
          "Identification documents",
          "Client project data and communications",
          "Contractual agreements and payment history",
          "Professional certifications and work samples",
        ],
      };

    case "stakeholder":
      return {
        description:
          "FreeTech's portal contains sensitive information pertaining to your business, such as:",
        sensitiveInfo: [
          "Company financial and project data",
          "Contracts and proprietary information",
          "Business strategic documents and communications",
          "Billing and payment processing information",
        ],
      };

    default:
      return {
        description:
          "FreeTech's portal contains sensitive information including:",
        sensitiveInfo: [
          "Personal and financial information",
          "Project data and communications",
          "Contractual documents and agreements",
        ],
      };
  }
};

export const MFAWarning: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const { userInfo, role } = useAuth();
  const navigate = useNavigate();
  const { isCurrentUserMfaEnrolled } = useMFA();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const hasShownWarning = sessionStorage.getItem("mfaWarningShown");

    // Don't show to students or if user doesn't have proper role
    const shouldShowWarning =
      userInfo &&
      role &&
      ["admin", "freelancer", "stakeholder"].includes(role) &&
      role !== "student" &&
      !isCurrentUserMfaEnrolled() &&
      !hasShownWarning &&
      userInfo.email !== "adam@freetech.co";

    if (shouldShowWarning) {
      setOpen(true);
    }
  }, [userInfo, role, isCurrentUserMfaEnrolled]);

  const handleAcknowledge = () => {
    sessionStorage.setItem("mfaWarningShown", "true");
    setOpen(false);
  };

  const handleEnableMFA = () => {
    setOpen(false);
    navigate("/account-settings");
  };

  if (!userInfo || !role) return null;

  const content = getUserTypeContent(role);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          p: isMobile ? 0 : 1,
          bgcolor: "background.paper",
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "warning.dark",
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 2,
          fontSize: isMobile ? "1.25rem" : "1.5rem",
        }}
      >
        <SecurityIcon
          color="warning"
          sx={{
            fontSize: isMobile ? "1.5rem" : "1.75rem",
          }}
        />
        Security Recommendation
      </DialogTitle>

      <DialogContent
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
        }}
      >
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 152, 0, 0.1)"
                : undefined,
            fontSize: isMobile ? "0.875rem" : "1rem",
          }}
        >
          Your account is not protected by Two-Factor Authentication (2FA)
        </Alert>

        <Typography
          variant="body1"
          paragraph
          color="text.primary"
          sx={{
            fontSize: isMobile ? "0.875rem" : "1rem",
            lineHeight: isMobile ? 1.4 : 1.6,
          }}
        >
          {content.description}
        </Typography>

        <Box sx={{ ml: isMobile ? 1 : 2, mb: 2 }}>
          {content.sensitiveInfo.map((item, index) => (
            <Typography
              key={index}
              variant="body1"
              component="div"
              sx={{
                mb: 1,
                fontSize: isMobile ? "0.875rem" : "1rem",
                lineHeight: isMobile ? 1.4 : 1.6,
              }}
              color="text.primary"
            >
              • {item}
            </Typography>
          ))}
        </Box>

        <Typography
          variant="body1"
          paragraph
          color="text.primary"
          sx={{
            fontSize: isMobile ? "0.875rem" : "1rem",
            lineHeight: isMobile ? 1.4 : 1.6,
          }}
        >
          To better protect your account and maintain the security of this
          sensitive data, we strongly recommend enabling Two-Factor
          Authentication (2FA).
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              color="warning"
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: isMobile ? "1.25rem" : "1.5rem",
                },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                lineHeight: isMobile ? 1.3 : 1.4,
              }}
            >
              I understand the security risks of not enabling 2FA and wish to
              proceed anyway
            </Typography>
          }
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 2,
          pt: isMobile ? 1 : 0,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 0,
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        <Button
          onClick={handleAcknowledge}
          color="inherit"
          disabled={!acknowledged}
          fullWidth={isMobile}
          sx={{
            order: isMobile ? 2 : 1,
            fontSize: isMobile ? "0.875rem" : "1rem",
            py: isMobile ? 1.5 : 1,
            color: "text.secondary",
            "&:hover": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Proceed Without 2FA
        </Button>
        <Button
          onClick={handleEnableMFA}
          variant="contained"
          color="warning"
          fullWidth={isMobile}
          sx={{
            order: isMobile ? 1 : 2,
            fontSize: isMobile ? "0.875rem" : "1rem",
            py: isMobile ? 1.5 : 1,
            "&:hover": {
              bgcolor: "warning.dark",
            },
          }}
        >
          Set up 2FA Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};
